import { Card, CardContent, Grid, Radio, Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import React, { memo, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useRetryableObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import HelpTip from 'v2/components/Shared/HelpTip';
import { Space } from 'v2/components/Shared/Space';
import useGetUser from 'v2/hooks/useGetUser';
import { EAnticipationStatus } from 'v2/interfaces/user';
import { customerFactory } from 'v2/services/customer';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { ConfirmationModal } from './ConfirmationModal';
import { useStyles } from './styles';

const AutomaticAnticipation = memo(() => {
  const classes = useStyles();
  const user = useGetUser();
  const [loading, setLoading] = useState(true);
  const [data, error, , retry] = useRetryableObservable(() => customerFactory.getById(user?.id || null), [user]);
  const [daysAnticipation, setDaysAnticipation] = useState(null);
  const [daysAnticipationSelected, setDaysAnticipationSelected] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<React.ReactNode>('');
  const [anticipationPromotion, , , retryAnticipationPromotion] = useRetryableObservable(() => financialService.getAnticipationPromotion(), []);
  const cannotAntecipate = eduzzAccountsService.verifyUserHasBetaModule('cannot-antecipate');
  const isLoading = data === undefined && error === undefined;

  useEffect(() => {
    if (data?.days_credit) {
      setDaysAnticipation(data.days_credit);
      setLoading(false);
    }
  }, [data]);

  const [changeDays] = useCallbackObservable(() => {
    setLoading(true);
    return customerFactory.changeAnticipationDays(daysAnticipationSelected).pipe(
      tap(
        () => {
          setLoading(false);
          setDaysAnticipation(daysAnticipationSelected);
          retry();
          handleConfirmationClose();
          toast.success(
            `Alteração realizada com sucesso! Sua antecipação automática está configurada para ${daysAnticipationSelected} dias após crédito em conta. ;)`
          );
        },
        (err) => {
          setLoading(false);
          toast.error(err);
          setDaysAnticipation(data.days_credit);
        }
      )
    );
  }, [daysAnticipationSelected]);

  const formatFee = (fee: number) => `${fee.toString().replace('.', ',')}%`;

  const getConfirmationMessage = (days: number) => {
    if (daysAnticipation === 2 && data?.custom_fee?.value) {
      return (
        <>
          Ao continuar você deixa de ter direito à taxa promocional no valor de <b>{formatFee(data?.custom_fee?.value)}</b> e passa a receber os
          valores de acordo com o novo prazo configurado e{' '}
          <em className={classes.commonFee}>
            taxa comum <Space axis='horizontal' size={4} /> <HelpTip text='2,99% + 0,1% por dia antecipado' />
          </em>
          . Deseja continuar?
        </>
      );
    }

    if (days === 2 && anticipationPromotion?.isActive) {
      return (
        <>
          Você está aderindo a taxa <b>promocional</b> de antecipação de 2 dias que é{' '}
          <b>{formatFee(anticipationPromotion.value)} + 0,1% por dia antecipado</b> e passa a receber os valores de acordo com o novo prazo
          configurado. Deseja continuar?{' '}
          <b>Essa promoção é válida até o dia 30/11/2022. Caso confirme, a taxa promocional terá vigência até 31/12/2022.</b>
        </>
      );
    }

    return `Os ganhos futuros com data de crédito superior a ${days} dias serão creditados em até 24h. Deseja realmente continuar?`;
  };

  const handleOpenConfirm = (days: number) => {
    retryAnticipationPromotion();
    setDaysAnticipationSelected(days);
    setConfirmationMessage(getConfirmationMessage(days));
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmationConfirm = () => {
    changeDays();
  };

  const handleConfirmationClose = () => {
    setIsConfirmationModalOpen(false);
    setDaysAnticipationSelected(null);
  };

  const isAnticipationBlocked = user?.anticipation_status === EAnticipationStatus.BLOQUEADO;

  if (cannotAntecipate) return null;

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationConfirm}
        message={confirmationMessage}
        submitting={loading}
      />

      <Card elevation={0} className={classes.rootInline}>
        <CardContent className={classes.rootInlineContent}>
          <Grid container alignItems='center' direction='row'>
            <Grid item xs={6} md={1}>
              <div className={classes.head}>
                <Typography color='inherit' component='h2' className={classes.title}>
                  Antecipação <br /> Automática
                </Typography>
              </div>
            </Grid>

            <Grid item xs={6} md={2}>
              <div>
                <Typography align='center' color='inherit' component='h2' variant='body2'>
                  Programe em quantos dias deseja receber seus ganhos.
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={5}>
              <div className={classes.inline}>
                <div className={classes.radio}>
                  2 dias
                  <Tooltip
                    title='Antecipação bloqueada por alto índice de chargeback'
                    placement='top'
                    arrow
                    disableFocusListener={!isAnticipationBlocked}
                    disableHoverListener={!isAnticipationBlocked}
                    disableTouchListener={!isAnticipationBlocked}
                  >
                    <div className={classes.tooltipRadioBtn}>
                      <Radio
                        id='2-days-radio-button'
                        disabled={loading || isLoading || isAnticipationBlocked}
                        checked={daysAnticipation === 2}
                        value={2}
                        color='primary'
                        onChange={() => handleOpenConfirm(2)}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className={classes.radio}>
                  7 dias
                  <Tooltip
                    title='Antecipação bloqueada por alto índice de chargeback'
                    placement='top'
                    arrow
                    disableFocusListener={!isAnticipationBlocked}
                    disableHoverListener={!isAnticipationBlocked}
                    disableTouchListener={!isAnticipationBlocked}
                  >
                    <div className={classes.tooltipRadioBtn}>
                      <Radio
                        id='7-days-radio-button'
                        disabled={loading || isLoading || isAnticipationBlocked}
                        checked={daysAnticipation === 7}
                        value={7}
                        color='primary'
                        onChange={() => handleOpenConfirm(7)}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className={classes.radio}>
                  14 dias
                  <Tooltip
                    title='Antecipação bloqueada por alto índice de chargeback'
                    placement='top'
                    arrow
                    disableFocusListener={!isAnticipationBlocked}
                    disableHoverListener={!isAnticipationBlocked}
                    disableTouchListener={!isAnticipationBlocked}
                  >
                    <div className={classes.tooltipRadioBtn}>
                      <Radio
                        id='14-days-radio-button'
                        disabled={loading || isLoading || isAnticipationBlocked}
                        checked={daysAnticipation === 14}
                        value={14}
                        color='primary'
                        onChange={() => handleOpenConfirm(14)}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className={classes.radio}>
                  21 dias
                  <Tooltip
                    title='Antecipação bloqueada por alto índice de chargeback'
                    placement='top'
                    arrow
                    disableFocusListener={!isAnticipationBlocked}
                    disableHoverListener={!isAnticipationBlocked}
                    disableTouchListener={!isAnticipationBlocked}
                  >
                    <div className={classes.tooltipRadioBtn}>
                      <Radio
                        id='21-days-radio-button'
                        disabled={loading || isLoading || isAnticipationBlocked}
                        checked={daysAnticipation === 21}
                        value={21}
                        color='primary'
                        onChange={() => handleOpenConfirm(21)}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className={classes.radio}>
                  <div className={classes.inline}>
                    <div>30 dias</div>
                    <Typography className={classes.defaultText}>(padrão)</Typography>
                  </div>

                  <Tooltip
                    title='Antecipação bloqueada por alto índice de chargeback'
                    placement='top'
                    arrow
                    disableFocusListener={!isAnticipationBlocked}
                    disableHoverListener={!isAnticipationBlocked}
                    disableTouchListener={!isAnticipationBlocked}
                  >
                    <div className={classes.tooltipRadioBtn}>
                      <Radio
                        id='30-days-radio-button'
                        disabled={loading || isAnticipationBlocked}
                        checked={daysAnticipation === 30}
                        value={30}
                        color='primary'
                        onChange={() => handleOpenConfirm(30)}
                      />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className={classes.tail}>
                <Typography color='textSecondary' component='h2' variant='body2' className={classes.tailText}>
                  * O recebimento antes do prazo incorre em taxa administrativa de{' '}
                  {data?.has_promotional_fee ? formatFee(data?.custom_fee?.value) : '2.99%'} + 0.1% por dia antecipado.{' '}
                  <a
                    href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Saiba mais.
                  </a>
                </Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
});

export default AutomaticAnticipation;
